<template>
  <div v-loading="pageLoading" class="ant-assistant-config">
    <div class="ant-assistant-config__card">
      <div class="ant-assistant-config__header">
        <div class="ant-assistant-config__header-icon">
          <i class="icon-arrow-left-right-line t-iconfont"></i>
        </div>
        <div class="ant-assistant-config__header-content">
          <div class="ant-assistant-config__title">数据同步配置</div>
          <div class="ant-assistant-config__desc">{{ assistantDesc }}</div>
        </div>
      </div>

      <div class="ant-assistant-config__body">
        <div
          v-if="showTemplateEmpty"
          class="ant-assistant-config__panel ant-assistant-config__panel--empty"
        >
          <Empty>
            <template #text>
              <div class="ant-assistant-config__empty-text">
                <span>当前暂无可用线索模板，</span>
                <span class="ant-assistant-config__empty-link" @click="handleOpenMarketManage"
                  >去开通</span
                >
                <span>「市场管理」应用</span>
              </div>
            </template>
          </Empty>
        </div>
        <div v-else class="ant-assistant-config__panel">
          <aside class="ant-assistant-config__side">
            <div
              v-for="template in templates"
              :key="template.value"
              class="ant-assistant-config__template"
              :class="{
                'ant-assistant-config__template--active': activeTemplate === template.value
              }"
              @click="handleTemplateChange(template.value)"
            >
              {{ template.label }}
            </div>
          </aside>

          <section class="ant-assistant-config__content">
            <div class="ant-assistant-config__mapping-card">
              <div class="ant-assistant-config__mapping-head">
                <div class="ant-assistant-config__source-title">
                  <img class="ant-assistant-config__source-icon" :src="drawerCoreInfo.icon" />
                  <span>{{ sourceFieldTitle }}</span>
                </div>
                <div class="ant-assistant-config__sync-label">同步到</div>
                <div class="ant-assistant-config__target-title">
                  <img class="ant-assistant-config__crm-icon" :src="xbbIcon" />
                  <span>{{ targetFieldTitle }}</span>
                </div>
              </div>

              <div class="ant-assistant-config__mapping-list">
                <div
                  v-for="field in fieldMappings"
                  :key="field.key"
                  class="ant-assistant-config__mapping-row"
                >
                  <div class="ant-assistant-config__field-info">
                    <div class="ant-assistant-config__field-name">{{ field.label }}</div>
                    <div class="ant-assistant-config__field-type">
                      {{ getFieldTypeName(field.fieldType) }}
                    </div>
                  </div>
                  <i class="ant-assistant-config__arrow icon-arrow-right-line t-iconfont"></i>
                  <el-select
                    v-model="field.value"
                    class="ant-assistant-config__select"
                    clearable
                    placeholder="请选择"
                  >
                    <el-option
                      v-for="option in getCustomerFieldOptions(field)"
                      :key="option.value"
                      :disabled="option.disabled"
                      :label="option.label"
                      :value="option.value"
                    />
                  </el-select>
                </div>
              </div>
            </div>

            <div class="ant-assistant-config__footer">
              <el-button
                color="#7a52f4"
                :disabled="!hasChanged"
                :loading="saveLoading"
                @click="handleSave"
                >保存</el-button
              >
              <el-button :disabled="!hasChanged" @click="handleReset">还原更改</el-button>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import xbbIcon from '@/assets/xbb.png'
import Empty from '@/components/Empty/index.vue'
import xbbSdk from 'xbb-sdk'
import { getFieldConfigList, saveFieldConfig } from '@/api/ai-setting'

defineOptions({
  name: 'AntAssistant'
})

const props = defineProps({
  drawerCoreInfo: {
    type: Object,
    required: true
  },
  settingDialogVisible: {
    type: Boolean,
    default: false
  }
})

const businessType = computed(() => {
  return props.drawerCoreInfo.alias === 'mybxAssistant' ? 8000 : 100
})

const fieldConfigType = computed(() => {
  return props.drawerCoreInfo.alias === 'mybxAssistant' ? 2 : 1
})

const sourceFieldTitle = computed(() => {
  return props.drawerCoreInfo.alias === 'mybxAssistant' ? '标讯字段' : '找客字段'
})

const targetFieldTitle = computed(() => {
  return props.drawerCoreInfo.alias === 'mybxAssistant' ? '线索字段' : '客户字段'
})

const assistantDesc = computed(() => {
  return props.drawerCoreInfo.alias === 'mybxAssistant'
    ? '将找客结果字段信息自动同步到 CRM 线索的对应字段信息。'
    : '将找客结果字段信息自动同步到 CRM 客户的对应字段信息。'
})

const showTemplateEmpty = computed(() => {
  return props.drawerCoreInfo.alias === 'mybxAssistant' && templates.value.length === 0
})

const templates = ref([])
const formList = ref([])
const sourceExplainList = ref([])
const activeTemplate = ref('')
const fieldMappings = ref([])
const savedMappings = ref([])
const saveLoading = ref(false)
const pageLoading = ref(false)

const fieldTypeNameMap = {
  1: '单行文本',
  2: '数字',
  4: '日期',
  7: '多行文本',
  10004: '描述文本',
  20003: '联系电话'
}

const customerFieldOptions = computed(() => {
  const form = formList.value.find((item) => item.formId === activeTemplate.value)
  return (form?.explainList || []).map((item) => ({
    label: item.attrName || item.name,
    value: item.attr,
    fieldType: item.fieldType
  }))
})

const getFieldTypeName = (fieldType) => {
  return fieldTypeNameMap[fieldType] || fieldType
}

const getCustomerFieldOptions = (field) => {
  const selectedValues = fieldMappings.value
    .filter((item) => item.key !== field.key)
    .map((item) => item.value)
    .filter(Boolean)

  return customerFieldOptions.value
    .filter((option) => option.fieldType === field.fieldType)
    .map((option) => ({
      ...option,
      disabled: selectedValues.includes(option.value)
    }))
}

const hasChanged = computed(() => {
  return fieldMappings.value.some((item, index) => item.value !== savedMappings.value[index].value)
})

const selectedForm = computed(() => {
  return formList.value.find((item) => item.formId === activeTemplate.value)
})

const createDefaultMappings = () => {
  return sourceExplainList.value.map((item) => ({
    key: item.attr,
    attr: item.attr,
    attrName: item.attrName,
    label: item.attrName,
    fieldType: item.fieldType,
    value: ''
  }))
}

const applyTemplateMappings = (form) => {
  const configMap = new Map(
    (form?.fieldConfig?.config || []).map((item) => [item.attr, item.linkAttr])
  )
  const mappings = createDefaultMappings().map((item) => ({
    ...item,
    value: configMap.get(item.attr) || ''
  }))

  fieldMappings.value = mappings
  savedMappings.value = mappings.map((item) => ({ ...item }))
}

const buildSaveConfig = () => {
  return fieldMappings.value
    .filter((item) => item.value)
    .map((item) => {
      const linkField =
        selectedForm.value?.explainList?.find((field) => field.attr === item.value) || {}
      return {
        attr: item.attr,
        name: item.attrName,
        linkAttr: linkField.attr,
        linkName: linkField.attrName
      }
    })
}

const saveMappings = () => {
  savedMappings.value = fieldMappings.value.map((item) => ({ ...item }))
}

const validateSaveConfig = (config) => {
  if (props.drawerCoreInfo.alias === 'myzkAssistant') {
    const hasCompanyName = config.some((item) => item.attr === 'company_name')
    if (!hasCompanyName) {
      ElMessage.error('企业名称字段必填')
      return false
    }
  }

  if (props.drawerCoreInfo.alias === 'mybxAssistant') {
    const hasNoticeName = config.some((item) => item.attr === 'name')
    if (!hasNoticeName) {
      ElMessage.error('标讯公告名称字段必填')
      return false
    }
  }

  return true
}

const handleOpenMarketManage = () => {
  xbbSdk.sendMessage('backXBBURL', { url: 'appModule/index' })
}

const handleSave = () => {
  if (saveLoading.value) {
    return Promise.resolve()
  }

  const config = buildSaveConfig()
  if (!validateSaveConfig(config)) {
    return Promise.resolve()
  }

  saveLoading.value = true
  return saveFieldConfig({
    formId: activeTemplate.value,
    type: fieldConfigType.value,
    businessType: businessType.value,
    config
  })
    .then((res) => {
      const currentForm = formList.value.find((item) => item.formId === activeTemplate.value)
      if (currentForm) {
        currentForm.fieldConfig = {
          ...(currentForm.fieldConfig || {}),
          config
        }
      }
      saveMappings()
      ElMessage.success(res.msg || '保存成功')
      return res
    })
    .finally(() => {
      saveLoading.value = false
    })
}

const confirmUnsavedChange = () => {
  if (!hasChanged.value) {
    return Promise.resolve('unchanged')
  }
  return ElMessageBox.confirm('检测到当前页面修改的内容尚未保存，是否保存？', '未保存更改', {
    cancelButtonText: '不保存',
    confirmButtonText: '保存',
    customClass: 'ant-assistant-config__confirm',
    distinguishCancelAndClose: true,
    type: ''
  })
    .then(() => {
      return handleSave().then(() => 'save')
    })
    .catch((action) => {
      if (action === 'cancel') {
        fieldMappings.value = savedMappings.value.map((item) => ({ ...item }))
        return 'discard'
      }
      return Promise.reject(action)
    })
}

const switchTemplate = (value) => {
  activeTemplate.value = value
  applyTemplateMappings(selectedForm.value)
}

const handleTemplateChange = (value) => {
  if (value === activeTemplate.value) {
    return
  }
  if (!hasChanged.value) {
    switchTemplate(value)
    return
  }

  confirmUnsavedChange()
    .then(() => {
      switchTemplate(value)
    })
    .catch(() => {})
}

const handleReset = () => {
  fieldMappings.value = savedMappings.value.map((item) => ({ ...item }))
}

const initFieldConfigList = () => {
  pageLoading.value = true
  return getFieldConfigList({
    businessType: businessType.value,
    type: fieldConfigType.value
  })
    .then((res) => {
      console.log('fieldConfigList:', res)
      const result = res.result || {}

      formList.value = result.formList || []
      sourceExplainList.value = result.defaultExplainList || []
      templates.value = formList.value.map((item) => ({
        label: item.name,
        value: item.formId
      }))
      activeTemplate.value = templates.value[0]?.value || ''
      if (activeTemplate.value) {
        applyTemplateMappings(selectedForm.value)
      } else {
        fieldMappings.value = createDefaultMappings()
        savedMappings.value = fieldMappings.value.map((item) => ({ ...item }))
      }

      return res
    })
    .finally(() => {
      pageLoading.value = false
    })
}

watch(
  () => props.settingDialogVisible,
  (visible) => {
    if (visible) {
      initFieldConfigList()
    }
  },
  {
    immediate: true
  }
)

defineExpose({
  confirmUnsavedChange
})
</script>

<style lang="scss" scoped>
.ant-assistant-config {
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 16px;
  box-sizing: border-box;
  background: #fff;

  &__card {
    width: 100%;
    max-width: 1120px;
    height: 100%;
    margin: 0 auto;
    border: 1px solid #e5e6eb;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 16px 0 rgba(50, 50, 51, 0.08);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__header {
    flex: 0 0 auto;
    height: 70px;
    padding: 12px;
    box-sizing: border-box;
    border-bottom: 1px solid #e5e6eb;
    display: flex;
    align-items: center;
  }

  &__header-icon {
    width: 32px;
    height: 32px;
    margin-right: 12px;
    border-radius: 8px;
    background: #fff;
    color: #323233;
    display: flex;
    align-items: center;
    justify-content: center;

    .t-iconfont {
      font-size: 20px;
      line-height: 1;
    }
  }

  &__header-content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin-bottom: 4px;
    color: #323233;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }

  &__desc {
    color: #969799;
    font-size: 14px;
    line-height: 20px;
  }

  &__body {
    flex: 1;
    min-height: 0;
    padding: 16px;
    box-sizing: border-box;
  }

  &__panel {
    height: 100%;
    border: 1px solid #f2f3f5;
    border-radius: 16px;
    display: flex;
    overflow: hidden;

    &--empty {
      align-items: center;
      justify-content: center;
    }
  }

  &__empty-text {
    margin-top: 16px;
    font-size: 14px;
    font-weight: normal;
    line-height: normal;
    color: #969799;
  }

  &__empty-link {
    color: #248df5;
    cursor: pointer;
  }

  &__side {
    width: 200px;
    flex: 0 0 auto;
    padding: 8px;
    box-sizing: border-box;
    border-right: 1px solid #f2f3f5;
  }

  &__template {
    height: 36px;
    padding: 8px 12px;
    box-sizing: border-box;
    border-radius: 8px;
    color: #323233;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    & + & {
      margin-top: 4px;
    }

    &:hover {
      background: #f2f3f5;
    }

    &--active {
      background: #f2f3f5;
      font-weight: 600;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__mapping-card {
    flex: 1;
    padding-bottom: 24px;
    box-sizing: border-box;
    overflow-y: auto;
  }

  &__mapping-head {
    width: 624px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    position: sticky;
    top: 0;
    background: #fff;
    padding-bottom: 16px;
    padding-top: 24px;
    z-index: 1;
  }

  &__source-title,
  &__target-title {
    width: 200px;
    display: flex;
    align-items: center;
    color: #646566;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__sync-label {
    margin: 0 48px;
    color: #9e9e9e;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }

  &__source-icon {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    border-radius: 50%;
    object-fit: cover;
  }

  &__crm-icon {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    object-fit: cover;
  }

  &__mapping-list {
    width: 624px;
    margin: 0 auto;
  }

  &__mapping-row {
    height: 58px;
    padding: 8px 0;
    box-sizing: border-box;
    border-bottom: 1px solid #f2f3f5;
    display: flex;
    align-items: center;
  }

  &__field-info {
    width: 200px;
    flex: 0 0 auto;
  }

  &__field-name {
    color: #323233;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__field-type {
    margin-top: 2px;
    color: #969799;
    font-size: 12px;
    line-height: 16px;
  }

  &__arrow {
    width: 16px;
    margin: 0 64px;
    color: #969799;
    font-size: 16px;
    line-height: 16px;
  }

  &__select {
    width: 280px;
    flex: 0 0 auto;

    :deep(.el-input__wrapper) {
      min-height: 36px;
      border-radius: 8px;
      box-shadow: 0 0 0 1px #e5e6eb inset;
    }
  }

  &__footer {
    flex: 0 0 auto;
    height: 68px;
    padding: 16px;
    box-sizing: border-box;
    border-top: 1px solid #f2f3f5;
    display: flex;
    align-items: center;

    :deep(.el-button) {
      height: 36px;
      padding: 8px 16px;
      border-radius: 12px;
      font-size: 14px;

      & + .el-button {
        margin-left: 12px;
      }
    }
  }
}
</style>

<style lang="scss">
.ant-assistant-config__confirm {
  width: 400px;
  padding: 0 !important;
  border: 0;
  border-radius: 24px !important;
  overflow: hidden;
  background: #fff;

  .el-message-box__header {
    padding: 24px 56px 24px 24px;
  }

  .el-message-box__title {
    color: #323233;
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
  }

  .el-message-box__headerbtn {
    top: 25px;
    right: 24px;
    width: 24px;
    height: 24px;
  }

  .el-message-box__close {
    color: #969799;
    font-size: 16px;
  }

  .el-message-box__content {
    padding: 0 24px;
  }

  .el-message-box__message {
    margin: 0;

    p {
      margin: 0;
      color: #323233;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
    }
  }

  .el-message-box__btns {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px 24px;
  }

  .el-button {
    height: 36px;
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;

    & + .el-button {
      margin-left: 12px;
    }
  }

  .el-button--default {
    color: #323233;
    border-color: #e5e6eb;
    background: #fff;
  }

  .el-button--primary {
    border-color: #7a52f4;
    background: #7a52f4;
    color: #fff;
  }
  .el-button--primary:hover {
    border-color: #7a52f4;
    background: #7a52f4;
  }
}
</style>
